"use client";

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { usePublicPathname } from "@/app/hooks/use-public-pathname";
import { SearchIcon as LucideSearchIcon } from "lucide-react";
import { SearchIcon } from "@/app/components/icons";
import { Input } from "@/app/components/ui/input";
import { NavButton } from "./nav-button";

type SearchResultT = {
  document_key: string;
  entity_type: string;
  entity_id: string | null;
  url: string;
  title: string;
  subtitle: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  thumbnail_url: string | null;
  thumbnail_alt: string | null;
  published_at: string | null;
};

type SearchResponseT = {
  results: SearchResultT[];
};

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;
const MAX_RESULTS = 15;

const TYPE_LABELS: Record<string, string> = {
  offer: "Angebot",
  campaign: "Aktion",
  post: "Beitrag",
  service: "Service",
  vehicle: "Gebrauchtwagen",
  contact_person: "Ansprechpartner",
  location: "Standort",
  static_page: "Seite",
};

type GlobalSearchProps = {
  isDarkTheme?: boolean;
};

export default function GlobalSearch({
  isDarkTheme = false,
}: GlobalSearchProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResultT[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [navBottom, setNavBottom] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pathname = usePublicPathname();
  const previousPathRef = useRef(pathname);
  const router = useRouter();
  const listboxId = useId();

  const trimmedQuery = useMemo(() => query.trim(), [query]);
  const canSearch = trimmedQuery.length >= MIN_QUERY_LENGTH;

  const resetState = useCallback(() => {
    setQuery("");
    setResults([]);
    setIsLoading(false);
    setHasSearched(false);
    setError(null);
    setHighlightedIndex(-1);
  }, []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) {
        resetState();
      }
    },
    [resetState]
  );

  useEffect(() => {
    if (!open) return;
    const timeoutId = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const updateNavBottom = () => {
      const navElement = containerRef.current?.closest("nav");
      if (!navElement) return;
      const rect = navElement.getBoundingClientRect();
      setNavBottom(rect.bottom);
    };

    updateNavBottom();
    window.addEventListener("resize", updateNavBottom);
    window.addEventListener("scroll", updateNavBottom, true);

    return () => {
      window.removeEventListener("resize", updateNavBottom);
      window.removeEventListener("scroll", updateNavBottom, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !canSearch) {
      setResults([]);
      setIsLoading(false);
      setHasSearched(false);
      setError(null);
      setHighlightedIndex(-1);
      return;
    }

    setIsLoading(true);
    setHasSearched(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await fetch(
          `/api/search?q=${encodeURIComponent(trimmedQuery)}&limit=${MAX_RESULTS}`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          throw new Error("Search request failed");
        }

        const data = (await response.json()) as SearchResponseT;
        if (!controller.signal.aborted) {
          setResults(Array.isArray(data.results) ? data.results : []);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("Global search failed", err);
        setError("Suche fehlgeschlagen. Bitte erneut versuchen.");
        setResults([]);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [canSearch, open, trimmedQuery]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [results]);

  useEffect(() => {
    if (!open) {
      previousPathRef.current = pathname;
      return;
    }

    if (previousPathRef.current !== pathname) {
      handleOpenChange(false);
    }

    previousPathRef.current = pathname;
  }, [handleOpenChange, open, pathname]);

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) {
        handleOpenChange(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [handleOpenChange, open]);

  const shouldShowDropdown =
    open && canSearch && (isLoading || hasSearched || error !== null);

  const handleEnterNavigation = useCallback(() => {
    if (highlightedIndex >= 0 && highlightedIndex < results.length) {
      router.push(results[highlightedIndex].url);
      handleOpenChange(false);
      return;
    }

    if (trimmedQuery.length > 0) {
      router.push(`/search?q=${encodeURIComponent(trimmedQuery)}`);
      handleOpenChange(false);
    }
  }, [handleOpenChange, highlightedIndex, results, router, trimmedQuery]);

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Escape") {
        handleOpenChange(false);
        return;
      }

      if (event.key === "ArrowDown") {
        if (results.length === 0) return;
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev >= results.length - 1 ? 0 : prev + 1
        );
        return;
      }

      if (event.key === "ArrowUp") {
        if (results.length === 0) return;
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev <= 0 ? results.length - 1 : prev - 1
        );
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        handleEnterNavigation();
      }
    },
    [handleEnterNavigation, handleOpenChange, results.length]
  );

  const resultsContent = (
    <>
      {isLoading && (
        <p className="text-muted-foreground text-sm" aria-live="polite">
          Suche läuft...
        </p>
      )}

      {!isLoading && error && (
        <p className="text-muted-foreground text-sm" aria-live="polite">
          {error}
        </p>
      )}

      {!isLoading && !error && hasSearched && results.length === 0 && (
        <p className="text-muted-foreground text-sm" aria-live="polite">
          Keine Suchergebnisse gefunden
        </p>
      )}

      {!isLoading && !error && results.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          className="scrollbar-hide flex max-h-72 flex-col gap-1 overflow-y-auto"
        >
          {results.map((result, index) => {
            const label = TYPE_LABELS[result.entity_type] ?? "Ergebnis";
            const isActive = index === highlightedIndex;
            return (
              <li
                key={result.document_key}
                id={`${listboxId}-option-${index}`}
                role="option"
                aria-selected={isActive}
              >
                <Link
                  href={result.url}
                  prefetch={false}
                  onClick={() => handleOpenChange(false)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`flex flex-col gap-1 rounded-md px-3 py-2 text-left transition ${
                    isDarkTheme
                      ? isActive
                        ? "bg-[#1A1A1A]"
                        : "hover:bg-[#1A1A1A]"
                      : isActive
                        ? "bg-accent"
                        : "hover:bg-accent"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={`text-sm font-medium ${
                        isDarkTheme ? "text-white" : "text-foreground"
                      }`}
                    >
                      {result.title}
                    </span>
                    <span
                      className={`rounded-full border px-2 py-0.5 text-[11px] ${
                        isDarkTheme
                          ? "border-white/25 text-white/70"
                          : "text-muted-foreground"
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                  {result.subtitle && (
                    <span
                      className={`text-xs ${
                        isDarkTheme ? "text-white/70" : "text-muted-foreground"
                      }`}
                    >
                      {result.subtitle}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );

  return (
    <div
      ref={containerRef}
      className="relative flex h-12 shrink-0 items-center"
      role="search"
    >
      <NavButton
        type="button"
        variant="link"
        linkSize="lg"
        onClick={() => handleOpenChange(!open)}
        aria-label={open ? "Suche schließen" : "Suche öffnen"}
        aria-expanded={open}
        className={`svg-crisp relative text-current ${
          isDarkTheme
            ? `${open ? "bg-[#1A1A1A]" : ""} hover:bg-[#1A1A1A]`
            : open
              ? "bg-accent"
              : ""
        }`}
      >
        <SearchIcon className="text-current" />
      </NavButton>

      {open && (
        <>
          <button
            type="button"
            className="fixed right-0 bottom-0 left-0 z-[70] bg-black/40"
            style={{ top: navBottom }}
            aria-label="Suche schließen"
            onClick={() => handleOpenChange(false)}
          />
          <div
            className="pointer-events-none fixed right-0 left-0 z-[90] px-4"
            style={{ top: navBottom }}
          >
            <div className="pointer-events-auto pt-3 pb-3 md:mx-auto md:max-w-2xl">
              <div className="relative w-full">
                <LucideSearchIcon className="absolute top-1/2 left-4 z-10 size-4 -translate-y-1/2 opacity-50" />
                <Input
                  ref={inputRef}
                  id="global-search-input"
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={handleInputKeyDown}
                  placeholder="Suchen..."
                  containerClassName="h-12 overflow-hidden"
                  className={`inline-block h-12 w-full pl-10 max-lg:h-[3.428571rem] max-lg:w-[114.285714%] max-lg:origin-top-left max-lg:scale-[0.875] max-lg:pl-[2.857rem] lg:text-sm ${
                    isDarkTheme
                      ? "border-white/20 bg-[#1A1A1A] text-white placeholder:text-white/60"
                      : "bg-muted"
                  }`}
                  aria-label="Globale Suche"
                  aria-expanded={shouldShowDropdown}
                  aria-controls={shouldShowDropdown ? listboxId : undefined}
                  aria-activedescendant={
                    highlightedIndex >= 0
                      ? `${listboxId}-option-${highlightedIndex}`
                      : undefined
                  }
                  aria-autocomplete="list"
                  autoComplete="off"
                />
              </div>
              {shouldShowDropdown && (
                <div
                  className={`mt-2 w-full rounded-md border p-3 shadow-md ${
                    isDarkTheme
                      ? "border-white/20 bg-black text-white"
                      : "bg-popover text-popover-foreground"
                  }`}
                >
                  {resultsContent}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
